import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getPopularity, getSearchCorrection } from "../../services/discoveryService";
import { applySeo, clip } from "../../utils/seo";
import { trackSearch, trackViewItemList } from "../../utils/analytics";
import Filter from "./Filter";
import ProductCard from "./ProductCard";
import {
  getProducts,
  getTextileProducts,
  getTextileFilters
} from "../../services/productService";

import "../../css/Allproduct.css";

// Short, factual intros for category pages (shown under the heading)
const INTROS = {
  saree: "Pattu, silk, cotton and fancy sarees — from Kanjivaram and Arani to everyday printed cottons.",
  kurti: "Easy kurtis in cotton, rayon and more, for work, college and every day.",
  "churidar set": "Churidar sets with dupatta in festive and everyday styles.",
  "short tops": "Short tops and tunics to pair with jeans, leggings or palazzos.",
  "t-shirt": "Comfortable women's T-shirts in cotton and blends.",
  bottoms: "Leggings, palazzos, pants and skirts in standard sizes.",
  nightwears: "Soft nighties and nightwear sets for a good night's sleep.",
  kids: "Everyday and party wear for boys, girls and babies.",
  women: "Sarees, kurtis, churidar sets, tops, bottoms and nightwear."
};

const AllProducts = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [popularity, setPopularity] = useState({});
  const [correction, setCorrection] = useState(null); // { from, to } when we fixed a typo       // URL-scoped (trending/new/bestseller/search)
  const [loading, setLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState({});
  const [selectedTag, setSelectedTag] = useState("ALL");
  const [pendingFilters, setPendingFilters] = useState({});
  const [activeFilters, setActiveFilters] = useState({});
  const [sortBy, setSortBy] = useState(() => new URLSearchParams(location.search).get("sort") || "relevance");
  const [maxPrice, setMaxPrice] = useState(10000);
  const [filterOpen, setFilterOpen] = useState(false);

  const [priceRange, setPriceRange] = useState({
    min: 0,
    max: 10000
  });

  useEffect(() => {
    setSelectedTag("ALL");
    setActiveFilters({});
    setPendingFilters({});

    loadProducts();
    loadFilterOptions();
  }, [location.search]);

  // Page title / description / indexing rules for this listing
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const search = qs.get("search");
    const sub = qs.get("subCategory");
    const cat = qs.get("category");
    const extraFilters = [...qs.keys()].some((k) => !["subCategory", "category"].includes(k));
    if (search) {
      applySeo({ title: `Search: ${clip(search, 40)} | Mithralaya`, robots: "noindex, follow", path: "/all-product" });
      return;
    }
    const dept = cat === "KIDS" ? "Kids" : cat === "WOMEN" ? "Women" : cat;
    const label = sub ? `${sub}${/s$/i.test(sub) ? "" : "s"}` : dept ? `${dept}'s wear` : "All products";
    const path = sub
      ? `/all-product?subCategory=${encodeURIComponent(sub)}`
      : cat ? `/all-product?category=${encodeURIComponent(cat)}` : "/all-product";
    applySeo({
      title: `${label} online | Mithralaya`,
      description: `Shop ${label.toLowerCase()} online at Mithralaya. New designs every week, cash on delivery and easy 7-day returns across India.`,
      path,
      robots: extraFilters ? "noindex, follow" : "index, follow"
    });
  }, [location.search]);

  // Units sold in the last 90 days, for "Popularity" sorting
  useEffect(() => {
    getPopularity().then((m) => setPopularity(m || {})).catch(() => {});
  }, []);

  const changeSort = (value) => {
    setSortBy(value);
    const qs = new URLSearchParams(location.search);
    if (value && value !== "relevance") qs.set("sort", value);
    else qs.delete("sort");
    navigate({ search: qs.toString() }, { replace: true });
  };

  useEffect(() => {
    document.body.style.overflow = filterOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [filterOpen]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams(location.search);
      const params = {};

      const search = qs.get('search');
      if (search) params.search = search;

      const category = qs.get('category');
      if (category) params.category = category;

      const subCategory = qs.get('subCategory');
      if (subCategory) params.subCategory = subCategory;

      const productType = qs.get('productType');
      if (productType) params.productType = productType;

      const sleeveOrStyle = qs.get('sleeveOrStyle');
      if (sleeveOrStyle) params.sleeveOrStyle = sleeveOrStyle;

      if (qs.get('new') === 'true') params.new = 'true';
      if (qs.get('bestseller') === 'true') params.bestseller = 'true';
      if (qs.get('trending') === 'true') params.trending = 'true';

      // Rendu fetches parallel-a:
      // 1. scoped — URL params-oda (trending/new/bestseller/search etc.)
      // 2. full catalog — filter apply pannumbodhu base-a use panna
      // Filters always narrow THIS page's products (e.g. only Kurtis on the
      // Kurti page). They used to switch to the whole catalogue, which is
      // why sarees appeared after filtering on a kurta page.
      const fetchAll = async (p) => {
        const [legacyData, textileData] = await Promise.all([
          getProducts(p),
          getTextileProducts(p)
        ]);
        return [...(textileData.products || []), ...(legacyData.products || [])];
      };

      let combined = await fetchAll(params);

      // Typo tolerance: "sareee" finds nothing -> try "saree"
      setCorrection(null);
      if (!combined.length && search) {
        try {
          const { correctedQuery } = await getSearchCorrection(search);
          if (correctedQuery) {
            const retry = await fetchAll({ ...params, search: correctedQuery });
            if (retry.length) {
              combined = retry;
              setCorrection({ from: search, to: correctedQuery });
            }
          }
        } catch (e) {
          /* keep empty result */
        }
      }


      setProducts(combined);
      if (search) trackSearch(search);
      trackViewItemList(search ? "Search results" : subCategory || category || "All products", combined);


      // slider max — full catalog price base-la
      const highest =
        combined.length > 0
          ? Math.max(...combined.map((p) => Number(p.price ?? p.retailPrice ?? 0)))
          : 10000;

      const safeMax = Math.ceil((highest || 10000) / 500) * 500 || 10000;
      setMaxPrice(safeMax);

      // price can come from the URL, e.g. home page "Under ₹999" shortcuts
      const urlMin = Math.max(0, Number(qs.get('minPrice')) || 0);
      const urlMax = Math.min(safeMax, Number(qs.get('maxPrice')) || safeMax);
      setPriceRange({ min: urlMin, max: Math.max(urlMin, urlMax) });

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const data = await getTextileFilters();
      setFilterOptions(data || {});
    } catch (err) {
      console.error(err);
    }
  };

  const filterProducts = (list, filters) => {
    return list.filter((product) => {
      return Object.entries(filters).every(([key, values]) => {
        if (!values || !values.length) return true;

        if (key === "sizes") {
          const productSizes = Array.isArray(product.sizes)
            ? product.sizes
            : product.sizes
              ? [product.sizes]
              : [];
          return values.some((v) =>
            productSizes.some(
              (s) => String(s).trim().toUpperCase() === String(v).trim().toUpperCase()
            )
          );
        }

        if (key === "colors") {
          const productColors = [
            ...(Array.isArray(product.colors) ? product.colors : []),
            ...(product.color ? [product.color] : [])
          ].map((c) => String(c).trim().toLowerCase());

          return values.some((v) =>
            productColors.includes(String(v).trim().toLowerCase())
          );
        }

        const productValue = product[key];
        if (Array.isArray(productValue)) {
          return values.some((v) =>
            productValue.some(
              (pv) => String(pv).trim().toLowerCase() === String(v).trim().toLowerCase()
            )
          );
        }

        if (productValue !== undefined && productValue !== null) {
          const pvStr = String(productValue).trim().toLowerCase();
          return values.some((v) => String(v).trim().toLowerCase() === pvStr);
        }

        return false;
      });
    });
  };

  const baseProducts = products;

  const filteredProducts = filterProducts(baseProducts, activeFilters);

  const getProductPrice = (p) => {
    const val = Number(p.price ?? p.retailPrice ?? 0);
    return isNaN(val) ? 0 : val;
  };

  const tagFilteredProducts = filteredProducts
    .filter((product) => {
      if (selectedTag === "ALL") return true;

      const tagLower = selectedTag.toLowerCase();
      return (
        String(product.sleeveOrStyle || "").toLowerCase() === tagLower ||
        String(product.productType || "").toLowerCase() === tagLower ||
        String(product.subCategory || "").toLowerCase() === tagLower
      );
    })
    .filter((product) => {
      const price = getProductPrice(product);
      return price >= priceRange.min && price <= priceRange.max;
    });

  let displayProducts = [...tagFilteredProducts];

  const discountOf = (p) => {
    const price = getProductPrice(p);
    const mrp = Number(p.mrp || 0);
    return mrp > price && mrp > 0 ? (mrp - price) / mrp : 0;
  };

  switch (sortBy) {
    case "price_asc":
    case "low":
      displayProducts.sort((a, b) => getProductPrice(a) - getProductPrice(b));
      break;
    case "price_desc":
    case "high":
      displayProducts.sort((a, b) => getProductPrice(b) - getProductPrice(a));
      break;
    case "new":
      displayProducts.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      break;
    case "popular":
      displayProducts.sort(
        (a, b) =>
          (popularity[b._id] || 0) - (popularity[a._id] || 0) ||
          Number(!!b.isBestSeller) - Number(!!a.isBestSeller)
      );
      break;
    case "discount":
      displayProducts.sort((a, b) => discountOf(b) - discountOf(a));
      break;
    default:
      break; // relevance: server order (best match first for searches)
  }

  // ---- Filter counts: how many products each option would show,
  // given the other filters and price already chosen ----
  const filterCounts = useMemo(() => {
    const inPrice = products.filter((p) => {
      const price = getProductPrice(p);
      return price >= priceRange.min && price <= priceRange.max;
    });
    const keys = ["sizes", "colors", "productType", "material", "sleeveOrStyle", "brand", "subCategory", "category"];
    const counts = {};
    keys.forEach((key) => {
      const others = { ...activeFilters };
      delete others[key];
      const pool = filterProducts(inPrice, others);
      const c = {};
      pool.forEach((p) => {
        let vals = [];
        if (key === "sizes") {
          vals = Array.isArray(p.sizes) ? p.sizes : p.sizes ? [p.sizes] : [];
        } else if (key === "colors") {
          vals = [
            ...(Array.isArray(p.colors) ? p.colors : []),
            ...(p.color ? [p.color] : [])
          ];
        } else {
          const v = p[key];
          vals = Array.isArray(v) ? v : v ? [v] : [];
        }
        [...new Set(vals.filter(Boolean))].forEach((x) => {
          c[x] = (c[x] || 0) + 1;
        });
      });
      counts[key] = c;
    });
    return counts;
  }, [products, activeFilters, priceRange.min, priceRange.max]);

  const activeFilterCount =
    Object.values(activeFilters).flat().length +
    (priceRange.min > 0 || priceRange.max < maxPrice ? 1 : 0);

  return (
    <>

      <div className="products-page">
        {filterOpen && (
          <div
            className="filter-backdrop"
            onClick={() => setFilterOpen(false)}
          />
        )}

        <aside className={`products-sidebar ${filterOpen ? "products-sidebar-open" : ""}`}>
          <Filter
            totalProducts={displayProducts.length}
            onFilterPreview={setPendingFilters}
            onFilterChange={setActiveFilters}
            isOpen={filterOpen}
            onClose={() => setFilterOpen(false)}
            maxPrice={maxPrice}
            priceRange={priceRange}
            onPriceChange={setPriceRange}
            counts={filterCounts}
            scope={{
              category: new URLSearchParams(location.search).get("category") || "",
              subCategory: new URLSearchParams(location.search).get("subCategory") || ""
            }}
          />
        </aside>

        <section className="products-content">
          {(() => {
            // Page heading, short intro, breadcrumb and count
            const qs = new URLSearchParams(location.search);
            const q = qs.get("search");
            const sub = qs.get("subCategory");
            const cat = qs.get("category");
            const type = qs.get("productType");
            const dept = cat === "KIDS" ? "Kids" : cat === "WOMEN" ? "Women" : cat;
            const title = q
              ? `Results for “${correction ? correction.to : q}”`
              : type || (sub ? `${sub}${/s$/i.test(sub) ? "" : "s"}` : dept ? `${dept}'s wear` : qs.get("new") ? "New arrivals" : qs.get("bestseller") ? "Best sellers" : qs.get("trending") ? "Trending now" : "All products");
            const intro = !q && INTROS[(sub || cat || "").toLowerCase()];
            const n = displayProducts.length;
            return (
              <header className="m-list-head">
                <nav className="m-crumbs" aria-label="Breadcrumb">
                  <Link to="/">Home</Link>
                  {sub && (
                    <>
                      <span aria-hidden="true">/</span>
                      {type ? <Link to={`/all-product?subCategory=${encodeURIComponent(sub)}`}>{sub}</Link> : <span>{sub}</span>}
                    </>
                  )}
                  {type && (<><span aria-hidden="true">/</span><span>{type}</span></>)}
                  {!sub && !type && (<><span aria-hidden="true">/</span><span>{q ? "Search" : title}</span></>)}
                </nav>
                <h1 className="m-list-title">{title}</h1>
                {intro && <p className="m-list-intro">{intro}</p>}
                {!loading && (
                  <p className="m-list-count" aria-live="polite">
                    {q ? `${n} result${n === 1 ? "" : "s"} for “${correction ? correction.to : q}”` : `${n} product${n === 1 ? "" : "s"}`}
                  </p>
                )}
              </header>
            );
          })()}

          <div className="products-header">
            <button
              type="button"
              className="mobile-filter-btn"
              onClick={() => setFilterOpen(true)}
              aria-label="Open filter options"
            >
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="mobile-filter-btn-badge">{activeFilterCount}</span>
              )}
            </button>

            <span className="m-result-count" />

            <label className="m-sort">
              <span>Sort by</span>
              <select
                className="sort-select"
                value={sortBy}
                onChange={(e) => changeSort(e.target.value)}
              >
                <option value="relevance">{new URLSearchParams(location.search).get("search") ? "Best match" : "Featured"}</option>
                <option value="popular">Popularity</option>
                <option value="new">Newest first</option>
                <option value="price_asc">Price: low to high</option>
                <option value="price_desc">Price: high to low</option>
                <option value="discount">Discount</option>
              </select>
            </label>
          </div>

          {correction && (
            <p className="m-correction" role="status">
              No results for “{correction.from}”. Showing results for <strong>“{correction.to}”</strong>.
            </p>
          )}

          {/* <div className="category-tags">
          <button
            className={selectedTag === "ALL" ? "active" : ""}
            onClick={() => setSelectedTag("ALL")}
          >
            ALL
          </button>

          {filterOptions.productType?.map((item) => (
            <button
              key={item}
              className={selectedTag === item ? "active" : ""}
              onClick={() => setSelectedTag(item)}
            >
              {item}
            </button>
          ))}

          {filterOptions.sleeveOrStyle?.map((item) => (
            <button
              key={item}
              className={selectedTag === item ? "active" : ""}
              onClick={() => setSelectedTag(item)}
            >
              {item}
            </button>
          ))}
        </div> */}

          {loading ? (
            <div className="no-results" role="status">Loading products…</div>
          ) : displayProducts.length === 0 ? (
            <div className="no-results">
              <p>No products match your search.</p>
              <p style={{ color: "#888" }}>
                Try a broader word such as <strong>saree</strong>, <strong>kurti</strong> or{" "}
                <strong>churidar</strong>, or clear some filters.
              </p>
            </div>
          ) : (
            <div className="products-grid">
              {displayProducts.map((product) => (
                <ProductCard
                  key={product._id}
                  product={product}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </>

  );
};

export default AllProducts;